/**
 * Импорт топа моделей из CRM (спека 2026-10-27-crm-top-import-design.md): чистая логика окна
 * «Импорт топ» — отметки, очередь по одной модели, итог. Без браузерного кода — гоняется node-тестами.
 */
import { initialChecked } from "./crmImport.ts";
import { plural } from "./videoRun.ts";
import type { CrmExcludedModel, CrmImportResult, CrmTopModel } from "./photos.ts";

export type QueueBody = { dress_id: number | null; import_ids: number[]; exclude_ids: number[] };
export type QueueItem = { model_id: number; body: QueueBody };
export type StepResult = ({ ok: true; result: CrmImportResult } | { ok: false; error: string }) & { model_id?: number };

/** Отмечены новые и упавшие фото «на человеке» у всех моделей топа. */
export function topInitialChecked(models: CrmTopModel[]): Set<number> {
  const out = new Set<number>();
  for (const m of models) for (const id of initialChecked(m.photos)) out.add(id);
  return out;
}

/** Отмеченные media_id модели в порядке сервера. */
export function modelChecked(model: CrmTopModel, checked: Set<number>): number[] {
  return model.photos.filter((ph) => checked.has(ph.media_id)).map((ph) => ph.media_id);
}

/** Потолок сервера на каждый из списков `import_ids`/`exclude_ids`. */
export const MAX_IDS = 500;

/** Тела запросов по моделям с отмеченным; больше 500 id модели режутся на несколько запросов. */
export function queueBodies(models: CrmTopModel[], checked: Set<number>, mode: "import" | "exclude"): QueueItem[] {
  const out: QueueItem[] = [];
  for (const m of models) {
    const ids = modelChecked(m, checked);
    for (let at = 0; at < ids.length; at += MAX_IDS) {
      const part = ids.slice(at, at + MAX_IDS);
      out.push({
        model_id: m.model_id,
        body: { dress_id: m.folder_id, import_ids: mode === "import" ? part : [], exclude_ids: mode === "exclude" ? part : [] },
      });
    }
  }
  return out;
}

/**
 * Выбор после обновления топа: уже виденные фото сохраняют отметку (снятую тоже), появившиеся — «на человеке» по умолчанию.
 */
export function reconcileChecked(prevChecked: Set<number>, prevSeen: Set<number>, models: CrmTopModel[]): Set<number> {
  const out = new Set<number>();
  for (const m of models)
    for (const ph of m.photos) {
      if (prevSeen.has(ph.media_id) ? prevChecked.has(ph.media_id) : initialChecked([ph]).has(ph.media_id)) out.add(ph.media_id);
    }
  return out;
}

/** По одной; ошибка модели не прерывает очередь; `shouldStop` смотрим перед каждой следующей. */
export async function runQueue(
  items: QueueItem[],
  send: (item: QueueItem) => Promise<CrmImportResult>,
  opts: { shouldStop: () => boolean; onStep: (i: number, res: StepResult) => void },
): Promise<{ done: number; stopped: boolean }> {
  let done = 0;
  for (let i = 0; i < items.length; i++) {
    if (opts.shouldStop()) return { done, stopped: true };
    let res: StepResult;
    try {
      res = { ok: true, result: await send(items[i]!) };
    } catch (e) {
      res = { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
    done++;
    opts.onStep(i, res);
  }
  return { done, stopped: false };
}

export function summarize(results: StepResult[]): { imported: number; excluded: number; failed: number; modelErrors: number } {
  let imported = 0;
  let excluded = 0;
  let failed = 0;
  const bad = new Set<number | string>();
  results.forEach((r, i) => {
    if (r.ok) {
      imported += r.result.imported;
      excluded += r.result.excluded;
      failed += r.result.failed.length;
    } else bad.add(r.model_id ?? `step${i}`);
  });
  return { imported, excluded, failed, modelErrors: bad.size };
}

/** Целое 1..500 (потолок сервера), иначе null. */
export function parseLimit(s: string): number | null {
  if (!/^\d+$/.test(s.trim())) return null;
  const n = Number(s);
  return n >= 1 && n <= 500 ? n : null;
}

/** Порция сетки: модели подряд, пока хватает бюджета плиток; модель без плиток не попадает, последняя может быть неполной. */
export function drawnBlocks(models: CrmTopModel[], budget: number): { model: CrmTopModel; photos: CrmTopModel["photos"] }[] {
  const out: { model: CrmTopModel; photos: CrmTopModel["photos"] }[] = [];
  let left = budget;
  for (const m of models) {
    if (left <= 0) break;
    if (m.photos.length === 0) continue;
    const photos = m.photos.slice(0, left);
    left -= photos.length;
    out.push({ model: m, photos });
  }
  return out;
}

/** Модели без локально скрытых (оптимистичное «Не импортировать модель»); без скрытых — тот же массив. */
export function visibleModels(models: CrmTopModel[], hidden: Set<number>): CrmTopModel[] {
  return hidden.size ? models.filter((m) => !hidden.has(m.model_id)) : models;
}

/** Отметки без фото модели. */
export function uncheckModel(checked: Set<number>, model: CrmTopModel): Set<number> {
  const out = new Set(checked);
  for (const ph of model.photos) out.delete(ph.media_id);
  return out;
}

export const excludedLabel = (e: CrmExcludedModel): string => e.series ?? e.name ?? `#${e.model_id}`;

/** Скрытые локально без возвращённой модели. */
export function unhide(hidden: Set<number>, id: number): Set<number> {
  const out = new Set(hidden);
  out.delete(id);
  return out;
}

/** ISO-дата/время → дд.мм.гггг (без пересчёта часового пояса); нечитаемое возвращается как есть. */
export function ruDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** Подпись сделок в шапке модели: недавние (по ним порядок топа) и за всё время. */
export function dealsLabel(m: CrmTopModel): string {
  const recent = `${m.deals_last} ${plural(m.deals_last, "недавняя", "недавних", "недавних")}`;
  return `${recent} · ${m.deals} ${plural(m.deals, "сделка", "сделки", "сделок")} всего`;
}
